import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Upload, Sparkles, ArrowRight, Camera, RotateCcw } from "lucide-react";

export default function Vision() {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  const onFile = (f) => {
    if (!f) return;
    if (!["image/png","image/jpeg","image/webp"].includes(f.type)) {
      toast.error("Sadece PNG / JPEG / WEBP");
      return;
    }
    if (f.size > 8 * 1024 * 1024) {
      toast.error("Dosya 8MB'dan büyük olamaz");
      return;
    }
    setFile(f);
    setPreview(URL.createObjectURL(f));
    setResult(null);
  };

  const reset = () => {
    setFile(null);
    setPreview(null);
    setResult(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
    if (cameraInputRef.current) cameraInputRef.current.value = "";
  };

  const analyze = async (targetFile) => {
    const f = targetFile || file;
    if (!f) return toast.error("Görsel seçin");
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await api.post("/ai/vision", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(r.data);
      toast.success("Analiz tamamlandı");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI hatası");
    } finally {
      setLoading(false);
    }
  };

  // Camera: mobile'da capture="environment" arka kamerayı direkt açar,
  // desktop'ta ise standart dosya seçici fallback olur.
  const onCameraShot = (f) => {
    if (!f) return;
    setFile(f);
    setPreview(URL.createObjectURL(f));
    setResult(null);
    // Auto-analyze — kullanıcı ürünü tartıdan indirmeden anında sonuç
    analyze(f);
  };

  return (
    <div className="space-y-8" data-testid="vision-page">
      <div>
        <div className="label-caps">Computer Vision</div>
        <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl mt-2 font-light">AI Vitrin Tanıma</h1>
        <p className="text-neutral-400 mt-3 max-w-2xl">
          Takıyı kameraya gösterin veya fotoğraf yükleyin. Yapay zeka modeli, ayar/karat, tahmini gram ve işçilik seviyesini
          otomatik tespit eder.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card-flat p-8" data-testid="vision-upload-card">
          <div className="label-caps mb-4">Görsel</div>
          <label
            className="block border border-dashed border-white/15 aspect-square flex items-center justify-center cursor-pointer hover:border-[--gold] transition-colors duration-200 overflow-hidden"
            data-testid="vision-dropzone"
          >
            {preview ? (
              <img src={preview} alt="preview" className="w-full h-full object-cover" />
            ) : (
              <div className="text-center p-8">
                <Upload className="mx-auto text-neutral-500 mb-3" size={28} />
                <div className="text-sm">Görseli buraya bırakın veya seçin</div>
                <div className="label-caps mt-2">PNG · JPG · WEBP · Maks 8MB</div>
              </div>
            )}
            <input ref={fileInputRef} type="file" accept="image/*" onChange={(e)=>onFile(e.target.files?.[0])} className="hidden" data-testid="vision-input" />
          </label>

          {/* Hidden camera-only input (mobile'da arka kamerayı açar) */}
          <input
            ref={cameraInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            onChange={(e) => onCameraShot(e.target.files?.[0])}
            className="hidden"
            data-testid="vision-camera-input"
          />

          <div className="grid grid-cols-2 gap-3 mt-6">
            <button
              onClick={() => cameraInputRef.current?.click()}
              disabled={loading}
              className="border border-[--gold] text-[--gold] px-4 py-3 flex items-center justify-center gap-2 hover:bg-[--gold] hover:text-black transition-colors disabled:opacity-40"
              data-testid="vision-camera-btn"
            >
              <Camera size={16} /> Fotoğraf Çek
            </button>
            <button
              onClick={() => analyze()}
              disabled={loading || !file}
              className="btn-primary-gold flex items-center justify-center gap-2"
              data-testid="vision-analyze"
            >
              <Sparkles size={14} /> {loading ? "Analiz…" : "Analiz Et"}
            </button>
          </div>

          {(file || result) && (
            <button
              onClick={reset}
              className="mt-3 w-full text-xs text-neutral-400 hover:text-white flex items-center justify-center gap-1.5 py-2 border border-white/10"
              data-testid="vision-reset"
            >
              <RotateCcw size={12} /> Yeniden Çek / Sıfırla
            </button>
          )}

          <p className="text-[10px] text-neutral-500 mt-4 font-mono tracking-wider leading-relaxed">
            📱 MOBİLDE "FOTOĞRAF ÇEK" ARKA KAMERAYI AÇAR VE ANINDA ANALİZ EDER.
          </p>
        </div>

        <div className="card-flat p-8" data-testid="vision-result-card">
          <div className="label-caps mb-4">Sonuç</div>
          {!result && !loading && (
            <div className="text-neutral-500 text-sm">
              Analiz için bir görsel yükleyin veya fotoğraf çekin. Sonuçlar burada gösterilecek.
            </div>
          )}
          {loading && (
            <div className="text-neutral-400 text-sm animate-pulse">Model tarafından değerlendiriliyor…</div>
          )}
          {result?.parsed && (
            <div className="space-y-4">
              <div className="font-display text-3xl" style={{color:"var(--gold)"}}>
                {result.parsed.type || "—"}
              </div>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <Info label="Kategori" value={result.parsed.category} />
                <Info label="Tahmini Ayar" value={result.parsed.estimated_purity_karat} />
                <Info label="Tahmini Gram" value={result.parsed.estimated_weight_gram} mono />
                <Info label="İşçilik" value={result.parsed.workmanship_level} />
                <Info label="Güven" value={`${result.parsed.confidence ?? 0}%`} mono />
              </div>
              {result.parsed.description && (
                <div className="border-t border-white/5 pt-4">
                  <div className="label-caps mb-2">Açıklama</div>
                  <p className="text-sm text-neutral-300 leading-relaxed">{result.parsed.description}</p>
                </div>
              )}
              <div className="label-caps pt-4 border-t border-white/5">Model: <span className="font-mono">{result.model}</span></div>
              <button
                onClick={() => {
                  const draft = {
                    name: `${result.parsed.type || "Ürün"} (AI)`,
                    category: result.parsed.category || "altin",
                    purity: (Number(result.parsed.estimated_purity_karat) || 22) / (Number(result.parsed.estimated_purity_karat) > 100 ? 1000 : 24),
                    net_weight: Number(result.parsed.estimated_weight_gram) || 1,
                    workmanship_cost: 0,
                    margin_rate: 0.15,
                    stock: 1,
                    from_vision: true,
                  };
                  sessionStorage.setItem("ai_vision_draft", JSON.stringify(draft));
                  toast.success("Ürün taslağı hazır. Ürünler sayfasına aktarılıyor…");
                  setTimeout(() => navigate("/products?from=vision"), 400);
                }}
                className="btn-primary-gold w-full mt-6 flex items-center justify-center gap-2"
                data-testid="vision-send-to-pos"
              >
                <ArrowRight size={14} /> Ürün Olarak Kaydet
              </button>
            </div>
          )}
          {result && !result.parsed && (
            <div>
              <div className="label-caps mb-2">Ham Yanıt</div>
              <pre className="text-xs text-neutral-400 whitespace-pre-wrap font-mono">{result.raw}</pre>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Info({ label, value, mono }) {
  return (
    <div>
      <div className="label-caps mb-1">{label}</div>
      <div className={mono ? "font-mono tabular-nums" : ""}>{value ?? "—"}</div>
    </div>
  );
}
